/*
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export type AppRole = 
  | 'super_admin'
  | 'admin'
  | 'inventory_manager'
  | 'order_processor'
  | 'customer_support'
  | 'content_manager'
  | 'financial_officer'
  | 'auditor'
  | 'customer';

// Utility type to represent the access matrix
type AccessMatrix = {
  [role in AppRole]?: {
    userManagement?: 'full' | 'view';
    productDatabase?: 'full' | 'edit' | 'view';
    orderProcessing?: 'full' | 'basic' | 'refunds';
    paymentGateways?: 'configure' | 'manage';
    contentManagement?: 'full' | 'edit';
    reporting?: 'all' | 'stock' | 'orders' | 'blog' | 'financial';
  };
};

// Access control matrix
const accessMatrix: AccessMatrix = {
  super_admin: {
    userManagement: 'full',
    productDatabase: 'full',
    orderProcessing: 'full',
    paymentGateways: 'configure',
    contentManagement: 'full',
    reporting: 'all',
  },
  admin: {
    userManagement: 'full',
    productDatabase: 'full',
    orderProcessing: 'full',
    paymentGateways: 'manage',
    contentManagement: 'full',
    reporting: 'all',
  },
  inventory_manager: {
    productDatabase: 'edit',
    reporting: 'stock',
  },
  order_processor: {
    orderProcessing: 'basic',
    reporting: 'orders',
  },
  customer_support: {
    userManagement: 'view',
    orderProcessing: 'refunds',
    reporting: 'orders',
  },
  content_manager: {
    contentManagement: 'full',
    reporting: 'blog',
  },
  financial_officer: {
    paymentGateways: 'manage',
    reporting: 'financial',
  },
  auditor: {
    reporting: 'all',
  },
  customer: {},
};

// Helper function to fetch user roles from the database
export const fetchUserRoles = async (userId: string): Promise<string[]> => {
  try {
    console.log('🔐 [fetchUserRoles] Starting role fetch for user:', userId);
    const startTime = Date.now();
    
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);

    const duration = Date.now() - startTime;
    console.log('🔐 [fetchUserRoles] Query completed in:', duration, 'ms');

    if (error) {
      console.error('🔐 [fetchUserRoles] Error fetching user roles:', error);
      return [];
    }

    if (!data || data.length === 0) {
      console.log('🔐 [fetchUserRoles] No roles found for user');
      return [];
    }

    const roles = data.map(item => item.role);
    console.log('🔐 [fetchUserRoles] Roles found:', roles);
    return roles;
  } catch (error) {
    console.error('🔐 [fetchUserRoles] Exception during role fetch:', error);
    return [];
  }
};

// Check if a user has a specific role
export const hasRole = (userRoles: string[], role: AppRole): boolean => {
  return userRoles.includes(role);
};

// Check if a user has any of the specified roles
export const hasAnyRole = (userRoles: string[], roles: AppRole[]): boolean => {
  return roles.some(role => userRoles.includes(role));
};

// Check if a user is an admin (including super admin)
export const isAdminUser = (userRoles: string[]): boolean => {
  const adminRoles = ['super_admin', 'admin', 'inventory_manager', 'order_processor', 'customer_support', 'content_manager', 'financial_officer'];
  return userRoles.some(role => adminRoles.includes(role));
};

// Check if a user is a super admin
export const isSuperAdminUser = (userRoles: string[]): boolean => {
  return userRoles.includes('super_admin');
};

// Access control functions
export const canAccessUserManagement = (userRoles: string[]): boolean => {
  return userRoles.includes('super_admin') || userRoles.includes('customer_support');
};

export const canAccessProductDatabase = (userRoles: string[], level: 'full' | 'edit' | 'view' = 'view'): boolean => {
  if (userRoles.includes('super_admin')) return true;
  if (userRoles.includes('inventory_manager')) {
    return true; // inventory_manager can access all levels
  }
  return false;
};

export const canAccessOrderProcessing = (userRoles: string[], level: 'full' | 'basic' | 'refunds' = 'basic'): boolean => {
  // Super admin can access everything
  if (userRoles.includes('super_admin')) return true;
  
  // Order processor can access full and basic
  if (userRoles.includes('order_processor')) {
    return level === 'full' || level === 'basic';
  }
  
  // Customer support can access refunds
  if (userRoles.includes('customer_support')) {
    return level === 'refunds';
  }
  
  return false;
};

export const canAccessPaymentGateways = (userRoles: string[], level: 'configure' | 'manage' = 'manage'): boolean => {
  if (userRoles.includes('super_admin')) return true;
  if (userRoles.includes('financial_officer')) {
    return true; // financial_officer can access all levels
  }
  return false;
};

export const canAccessContentManagement = (userRoles: string[]): boolean => {
  return userRoles.includes('super_admin') || userRoles.includes('content_manager');
};

export const canAccessReporting = (userRoles: string[], type: 'all' | 'stock' | 'orders' | 'blog' | 'financial'): boolean => {
  if (userRoles.includes('super_admin') || userRoles.includes('auditor')) return true;
  
  if (type === 'stock') return userRoles.includes('inventory_manager');
  if (type === 'orders') return userRoles.includes('order_processor') || userRoles.includes('customer_support');
  if (type === 'blog') return userRoles.includes('content_manager');
  if (type === 'financial') return userRoles.includes('financial_officer');
  
  return false;
};

export const getAllRoles = (): AppRole[] => {
  return [
    'super_admin',
    'admin',
    'inventory_manager',
    'order_processor',
    'customer_support',
    'content_manager',
    'financial_officer',
    'auditor',
    'customer'
  ];
};
*/
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export type AppRole =
  | 'super_admin'
  | 'admin'
  | 'inventory_manager'
  | 'order_processor'
  | 'customer_support'
  | 'content_manager'
  | 'financial_officer'
  | 'auditor'
  | 'customer';

// Utility type to represent the access matrix
type AccessMatrix = {
  [role in AppRole]?: {
    userManagement?: 'full' | 'view';
    productDatabase?: 'full' | 'edit' | 'view';
    orderProcessing?: 'full' | 'basic' | 'refunds';
    paymentGateways?: 'configure' | 'manage';
    contentManagement?: 'full' | 'edit';
    reporting?: 'all' | 'stock' | 'orders' | 'blog' | 'financial';
  };
};

// Access control matrix
const accessMatrix: AccessMatrix = {
  super_admin: {
    userManagement: 'full',
    productDatabase: 'full',
    orderProcessing: 'full',
    paymentGateways: 'configure',
    contentManagement: 'full',
    reporting: 'all',
  },
  admin: {
    userManagement: 'full',
    productDatabase: 'full',
    orderProcessing: 'full',
    paymentGateways: 'manage',
    contentManagement: 'full',
    reporting: 'all',
  },
  inventory_manager: {
    productDatabase: 'edit',
    reporting: 'stock',
  },
  order_processor: {
    orderProcessing: 'basic',
    reporting: 'orders',
  },
  customer_support: {
    userManagement: 'view',
    orderProcessing: 'refunds',
    reporting: 'orders',
  },
  content_manager: {
    contentManagement: 'full',
    reporting: 'blog',
  },
  financial_officer: {
    paymentGateways: 'manage',
    reporting: 'financial',
  },
  auditor: {
    reporting: 'all',
  },
  customer: {},
};

// Helper function to fetch user roles from the database
export const fetchUserRoles = async (userId: string): Promise<string[]> => {
  try {
    console.log('🔐 [fetchUserRoles] Starting role fetch for user:', userId);
    const startTime = Date.now();

    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);

    const duration = Date.now() - startTime;
    console.log('🔐 [fetchUserRoles] Query completed in:', duration, 'ms');

    if (error) {
      console.error('🔐 [fetchUserRoles] Error fetching user roles:', error);
      return [];
    }

    if (!data || data.length === 0) {
      console.log('🔐 [fetchUserRoles] No roles found for user');
      return [];
    }

    const roles = data.map(item => item.role);
    console.log('🔐 [fetchUserRoles] Roles found:', roles);

    return roles;
  } catch (error) {
    console.error('🔐 [fetchUserRoles] Exception during role fetch:', error);
    return [];
  }
};

// Check if a user has a specific role
export const hasRole = (userRoles: string[], role: AppRole): boolean => {
  return userRoles.includes(role);
};

// Check if a user has any of the specified roles
export const hasAnyRole = (userRoles: string[], roles: AppRole[]): boolean => {
  return roles.some(role => userRoles.includes(role));
};

// Check if a user is an admin (including super admin)
export const isAdminUser = (userRoles: string[]): boolean => {
  const adminRoles = [
    'super_admin',
    'admin',
    'inventory_manager',
    'order_processor',
    'customer_support',
    'content_manager',
    'financial_officer'
  ];
  return userRoles.some(role => adminRoles.includes(role));
};

// Check if a user is a super admin
export const isSuperAdminUser = (userRoles: string[]): boolean => {
  return userRoles.includes('super_admin');
};

// Access control functions
export const canAccessUserManagement = (userRoles: string[]): boolean => {
  // Only super_admin can access user management
  return userRoles.includes('super_admin');
};

export const canAccessProductDatabase = (
  userRoles: string[],
  level: 'full' | 'edit' | 'view' = 'view'
): boolean => {
  if (userRoles.includes('super_admin') || userRoles.includes('admin')) return true;

  if (userRoles.includes('inventory_manager')) {
    return true; // inventory_manager can access all levels
  }

  return false;
};

export const canAccessOrderProcessing = (
  userRoles: string[],
  level: 'full' | 'basic' | 'refunds' = 'basic'
): boolean => {
  // Super admin and admin can access everything
  if (userRoles.includes('super_admin') || userRoles.includes('admin')) return true;

  // Order processor can access full and basic
  if (userRoles.includes('order_processor')) {
    return level === 'full' || level === 'basic';
  }

  // Customer support can access refunds
  if (userRoles.includes('customer_support')) {
    return level === 'refunds';
  }

  return false;
};

export const canAccessPaymentGateways = (
  userRoles: string[],
  level: 'configure' | 'manage' = 'manage'
): boolean => {
  if (userRoles.includes('super_admin')) return true;
  
  if (userRoles.includes('admin') || userRoles.includes('financial_officer')) {
    return true; // Both can access all levels
  }

  return false;
};

export const canAccessContentManagement = (userRoles: string[]): boolean => {
  return userRoles.includes('super_admin') || 
         userRoles.includes('admin') || 
         userRoles.includes('content_manager');
};

export const canAccessReporting = (
  userRoles: string[],
  type: 'all' | 'stock' | 'orders' | 'blog' | 'financial'
): boolean => {
  if (userRoles.includes('super_admin') || 
      userRoles.includes('admin') || 
      userRoles.includes('auditor')) return true;

  if (type === 'stock') return userRoles.includes('inventory_manager');
  if (type === 'orders') return userRoles.includes('order_processor') || userRoles.includes('customer_support');
  if (type === 'blog') return userRoles.includes('content_manager');
  if (type === 'financial') return userRoles.includes('financial_officer');

  return false;
};

export const getAllRoles = (): AppRole[] => {
  return [
    'super_admin',
    'admin',
    'inventory_manager',
    'order_processor',
    'customer_support',
    'content_manager',
    'financial_officer',
    'auditor',
    'customer'
  ];
};